import React, { useState, useEffect } from 'react';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

const ProductImages = ({
  productData,
  currentImageIndex,
  setCurrentImageIndex,
  handlePrevImage,
  handleNextImage,
  handleTouchStart,
  handleTouchEnd,
  handleTouchMove,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setImageLoaded(false); // reset loading state on image change
  }, [currentImageIndex]);

  // Local touch handling for better scroll control
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });
  const [isSwiping, setIsSwiping] = useState(false);

  const handleLocalTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
    setIsSwiping(false);
  };

  const handleLocalTouchMove = (e) => {
    if (!e.touches[0]) return;
    
    const touch = e.touches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
    
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    
    // Only prevent default if it's clearly a horizontal swipe
    if (deltaX > deltaY && deltaX > 30) {
      setIsSwiping(true);
      e.preventDefault();
    } else if (deltaY > deltaX) {
      // Allow vertical scrolling
      setIsSwiping(false);
    }
  };

  const handleLocalTouchEnd = (e) => {
    if (!isSwiping) return;
    
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);
    
    // Only change images if it was a clear horizontal swipe
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > deltaY) {
      if (deltaX > 0) {
        handleNextImage();
      } else {
        handlePrevImage();
      }
    }
    
    setIsSwiping(false);
  };

  return (
    <div className="w-full sm:w-[80%] relative">
      <div
        className="w-full flex justify-center relative main-image-container"
        onTouchStart={handleLocalTouchStart}
        onTouchMove={handleLocalTouchMove}
        onTouchEnd={handleLocalTouchEnd}
      >
        <div className="flex w-full justify-center items-center min-h-[320px] sm:min-h-[400px] relative">
          {/* Skeleton Loader */}
          {!imageLoaded && (
            <div className="absolute w-full h-full bg-gray-200 animate-pulse rounded-md" />
          )}

          <Zoom>
            <img
              src={productData.image[currentImageIndex]}
              alt={`${productData.name}-${currentImageIndex}`}
              onLoad={() => setImageLoaded(true)}
              className={`w-full h-auto object-contain rounded-md shadow flex-shrink-0 transition-opacity duration-300 ease-in-out ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </Zoom>
        </div>

        {/* Left Arrow */}
        <button
          onClick={handlePrevImage}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-[#4c0f2e] p-2 rounded-lg shadow-sm border border-[#f4d9c4] hover:bg-[#3a0b22] transition-colors duration-200"
          aria-label="Previous Image"
        >
          <svg
            className="w-4 h-4 text-[#f4d9c4]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          onClick={handleNextImage}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#4c0f2e] p-2 rounded-lg shadow-sm border border-[#f4d9c4] hover:bg-[#3a0b22] transition-colors duration-200"
          aria-label="Next Image"
        >
          <svg
            className="w-4 h-4 text-[#f4d9c4]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Dot Indicators */}
      <div className="flex justify-center mt-4 gap-2">
        {productData.image.map((_, index) => (
          <div
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-all duration-300 ${
              currentImageIndex === index
                ? 'bg-[#4c0f2e] shadow-sm border border-[#f4d9c4] scale-125'
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductImages;